import _ from 'underscore';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { CustomDropdown } from '../custom-dropdown-component';
import { getVideoDurationFilterStyle } from './style';

export const VideoDurationFilter = ({
  defaultMinDuration,
  defaultMaxDuration,
  dropdownWidth,
  onChange,
  onDismiss,
  focused,
  i18n,
}) => {
  // use ref to handle focus switching when pressing tab key
  const minDurationRef = useRef(null);
  const maxDurationRef = useRef(null);

  const classes = useDefaultStyles(getVideoDurationFilterStyle);

  const [minDuration, setMinDuration] = useState(defaultMinDuration);
  const [maxDuration, setMaxDuration] = useState(defaultMaxDuration);

  const [minDurationInvalid, setMinDurationInvalid] = useState(false);
  const [maxDurationInvalid, setMaxDurationInvalid] = useState(false);

  const onChangeCallback = useCallback((setter, newValue) => {
    const val = parseInt(newValue, 10);
    if (_.isEmpty(newValue)) {
      setter(null);
      return;
    }
    if (!_.isNaN(val) && val >= 0 && val <= 1000000) {
      setter(val);
    }
  }, []);

  const onMinDurationChange = useCallback((event, newValue) => {
    onChangeCallback(setMinDuration, newValue);
    if (_.isEmpty(newValue) || maxDuration > newValue) {
      setMinDurationInvalid(false);
      setMaxDurationInvalid(false);
    } else if ((maxDuration && newValue >= maxDuration)) {
      setMinDurationInvalid(true);
    } else {
      setMinDurationInvalid(false);
    }
  }, [maxDuration, onChangeCallback]);

  const onMaxDurationChange = useCallback((event, newValue) => {
    onChangeCallback(setMaxDuration, newValue);
    if (_.isEmpty(newValue) || newValue > minDuration) {
      setMinDurationInvalid(false);
      setMaxDurationInvalid(false);
    } else if (newValue <= minDuration) {
      setMaxDurationInvalid(true);
    } else {
      setMaxDurationInvalid(false);
    }
  }, [onChangeCallback, minDuration]);

  const onClear = useCallback((isDropdownOpen) => {
    setMinDuration(null);
    setMaxDuration(null);
    if (!isDropdownOpen && (defaultMinDuration || defaultMaxDuration)) {
      // trigger onchange only when dropdown list is closed
      onChange({ newMinDuration: null, newMaxDuration: null });
    }
    if (minDurationRef.current) {
      minDurationRef.current.focus();
    }
  }, [defaultMinDuration, defaultMaxDuration, onChange]);

  const title = useMemo(() => {
    if (minDuration && maxDuration && !minDurationInvalid && !maxDurationInvalid) {
      return `${minDuration}s < ${i18n.getString(_TL_('Duration'))} < ${maxDuration}s`;
    }
    if (minDuration && !minDurationInvalid) {
      return `${i18n.getString(_TL_('Duration'))} > ${minDuration}s`;
    }
    if (maxDuration && !maxDurationInvalid) {
      return `${i18n.getString(_TL_('Duration'))} < ${maxDuration}s`;
    }
    return '';
  }, [minDuration, maxDuration, minDurationInvalid, maxDurationInvalid, i18n]);

  const focusTextField = useCallback(textFieldRef => (event) => {
    if (event.key === 'Tab') {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
      if (event.stopPropagation) { event.stopPropagation(); }
      if (event.preventDefault) { event.preventDefault(); }
    }
  }, []);

  const onMinDurationKeyUp = useMemo(() => focusTextField(minDurationRef), [focusTextField]);
  const onMaxDurationKeyUp = useMemo(() => focusTextField(maxDurationRef), [focusTextField]);

  const onDropdownDismiss = useCallback(() => {
    if (defaultMinDuration !== minDuration || defaultMaxDuration !== maxDuration) {
      onChange({ newMinDuration: minDurationInvalid ? null : minDuration, newMaxDuration: maxDurationInvalid ? null : maxDuration }, title);
    }
    if (onDismiss) {
      onDismiss({ newMinDuration: minDurationInvalid ? null : minDuration, newMaxDuration: maxDurationInvalid ? null : maxDuration }, title);
    }
  }, [defaultMinDuration, minDuration, defaultMaxDuration, maxDuration, onDismiss, onChange, minDurationInvalid, maxDurationInvalid, title]);

  return (
    <CustomDropdown
      title={title}
      placeholder={i18n.getString(_TL_('Video duration'))}
      dropdownWidth={dropdownWidth}
      onClear={onClear}
      onDismiss={onDropdownDismiss}
      focused={focused}
    >
      <div className={classes.videoDurationFilterDowndown}>
        <div className={classes.dropdownTitle}>{i18n.getString(_TL_('Video duration'))}</div>
        <TextField
          ref={minDurationRef}
          className={classes.dropdownValueInput}
          label={i18n.getString(_TL_('Min. duration'))}
          suffix="s"
          value={_.isNumber(minDuration) ? minDuration : ''}
          onChange={onMinDurationChange}
          onKeyDown={onMinDurationKeyUp}
          invalid={minDurationInvalid}
        />
        <TextField
          ref={maxDurationRef}
          className={classes.dropdownValueInput}
          label={i18n.getString(_TL_('Max. duration'))}
          suffix="s"
          value={_.isNumber(maxDuration) ? maxDuration : ''}
          onChange={onMaxDurationChange}
          onKeyDown={onMaxDurationKeyUp}
          invalid={maxDurationInvalid}
        />
      </div>
    </CustomDropdown>
  );
};

VideoDurationFilter.propTypes = {
  defaultMinDuration: PropTypes.number,
  defaultMaxDuration: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onDismiss: PropTypes.func,
  focused: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

VideoDurationFilter.defaultProps = {
  dropdownWidth: 0,
  defaultMinDuration: null,
  defaultMaxDuration: null,
  onDismiss: null,
  focused: false,
};
